/**
 * GoogieShell — the sky the whole redesigned app sits in.
 *
 * GOOGIE-1. One component owns the whole backdrop so no screen paints
 * its own: the time-of-day gradient, the star field, the atomic
 * starburst, the comet, the soft corner glow, and the floating nav
 * rail at the bottom.
 *
 * GOOGIE-2 moved it UP, from ten screens to the shell. It now wraps
 * the router outlet in RootLayout, which is what makes retiring the
 * old design possible: a page nobody has redesigned yet still lands
 * under the sky with working navigation instead of being stranded on
 * a bare white route. Screens render content; the shell renders the
 * world. Two consequences worth knowing:
 *
 *   • The sky is shared state, so Today's "tap to cycle" chip reaches
 *     it through GoogieSky rather than by owning a shell of its own.
 *   • The bottom chrome is Orbit's own Aperture Bar, not a rail of
 *     the redesign's invention — see the note at the bar below.
 *   • `.googie` sets the sky and the tokens, but NOT type or colour —
 *     those live on `.g-screen` (see GoogieScreen). Otherwise every
 *     not-yet-redesigned page would inherit Bricolage and ivory over
 *     its own palette and look broken rather than merely old.
 *
 * The sky follows the studio's clock (dawn → midday → golden hour →
 * night). Stars fade out at midday; the comet only crosses a dark sky.
 *
 * SEASON-1 — in October and December (Seasonal themes, on by default)
 * the season takes the sky over: its painted scene for the hour in
 * place of the gradient, the stars, the burst and the comet, and its
 * colours on every `--g-*` token, here and on <html> for the dialogs.
 * The paintings are night-palette work, so they come with Dark mode;
 * in Light mode the paper stays paper and the season shows in Nova,
 * Today's greeting and quick actions, and the bar's light. See
 * src/lib/seasons.ts.
 */
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useGoogleFonts } from '@/components/gallery/templates/useGoogleFonts';
import { SKIES, googieFontsHref, resolveTod, skyVars, type Tod } from '@/lib/googie';
import { SEASON_AURA, seasonCssVars, type Season } from '@/lib/seasons';
import { publishActiveSeason, useSeason } from '@/hooks/useSeasonalThemes';
import { AppApertureBar } from '@/components/shared/AppApertureBar';
import { SeasonalSky } from './seasonal/SeasonalSky';

interface GoogieSkyValue {
  tod: Tod;
  sky: (typeof SKIES)[Tod];
  /** Paper ground rather than night sky. */
  light: boolean;
  /** SEASON-1 — the season showing, if any (Halloween, Christmas). */
  season: Season | null;
  /** True when the side rail is carrying the chrome instead of the
   *  Aperture Bar. GOOGIE-16 — screens read this so they don't draw a
   *  second Orbit mark beside the one already at the top of the rail. */
  railed: boolean;
  /** Pin the sky to one time of day, ignoring the clock. */
  setTod: (next: Tod) => void;
  /** Step to the next sky — Today's chip. */
  cycle: () => void;
}

const TOD_ORDER: Tod[] = ['dawn', 'midday', 'dusk', 'night'];

const GoogieSkyContext = createContext<GoogieSkyValue | null>(null);

/** The sky, from anywhere under the shell. */
export function useGoogieSky(): GoogieSkyValue {
  const ctx = useContext(GoogieSkyContext);
  if (ctx) return ctx;
  // A screen rendered outside the shell (a test, a harness) still
  // needs a sky to read; give it the clock's, read-only.
  const tod = resolveTod();
  return { tod, sky: SKIES[tod], light: false, season: null, railed: false, setTod: () => {}, cycle: () => {} };
}

export interface GoogieShellProps {
  children: ReactNode;
  /** Light mode swaps the sky for paper and drops the stars. */
  light?: boolean;
  /** Hide the bottom bar (the launch sequence, a full-bleed board). */
  bare?: boolean;
  className?: string;
}

/**
 * GOOGIE-FROST-1 — the routes that keep the sky at full strength.
 * Everything else gets the frosted pane. A list rather than a prop so
 * a page cannot forget to ask: the default is the quiet one.
 *
 * '/' is TODAY. It was missed on the first pass because every other
 * screen here is a named route and Today is the bare root, so a list
 * written by reading the sidebar skips straight past it — which is
 * exactly the page where the sky matters most (Rennan: "the frosted
 * background is now on the today page… those two pages should always
 * show the theme"). It is the app's weather report; the sky is the
 * content, not the backdrop.
 *
 * Nova was already here. Calendar stays because it was asked for
 * separately and nothing has retracted it.
 *
 * Matched exactly for '/', never by prefix — `startsWith('/')` is
 * every route in the app.
 */
const UNFROSTED = ['/', '/assistant', '/calendar'];

export function GoogieShell({ children, light = false, bare = false, className }: GoogieShellProps) {
  const { pathname } = useLocation();
  const frosted = !UNFROSTED.some((p) => pathname === p || (p !== '/' && pathname.startsWith(p + '/')));
  useGoogleFonts(googieFontsHref);
  // SEASON-1 — the season, when one is showing, and whether it paints
  // the sky. Only over the night palette: a studio that chose Light
  // mode keeps its paper (half the app reads the mode for itself —
  // logos, the rail, portals — and a dark painting under all of that
  // is a fight, not a theme). Nova, the greeting and the bar's light
  // still dress up.
  const season = useSeason();
  const painted = light ? null : season;
  // Published before paint, so everything that reads it — Nova
  // anywhere in the app, the launcher included — is dressed on the
  // first frame.
  useLayoutEffect(() => {
    publishActiveSeason(season);
    return () => publishActiveSeason(null);
  }, [season]);
  // GOOGIE-4 — every dialog in Orbit is a Radix portal, and a portal
  // attaches to <body>, OUTSIDE this wrapper. Without this, the sky
  // and the tokens stop at the page and the first dialog a studio
  // opens — a lead, a client, a new gallery — comes up in the app's
  // light theme on top of the redesign. So the scope goes on <html>
  // too, and comes off again when the redesign is switched off.
  useEffect(() => {
    const el = document.documentElement;
    const hadDark = el.classList.contains('dark');
    el.classList.add('googie');
    // GOOGIE-9 — `dark` is no longer unconditional. It used to be,
    // which is why the Appearance toggle moved and nothing happened:
    // a studio could pick Light and the redesign would keep painting
    // night at them. The mode now comes from their own preference
    // (RootLayout reads it) and this follows it, portals included.
    el.classList.toggle('dark', !light);
    el.setAttribute('data-g-mode', light ? 'light' : 'dark');
    return () => {
      el.classList.remove('googie');
      el.removeAttribute('data-g-mode');
      if (!hadDark) el.classList.remove('dark');
    };
  }, [light]);

  // SEASON-1 — the season's tokens on <html> too, for the same reason
  // as GOOGIE-4: a dialog is a portal on <body>, outside the shell, and
  // would otherwise open in the year-round colours over a Halloween app.
  // Whatever was inline before goes back after.
  useEffect(() => {
    if (!painted) return;
    const el = document.documentElement;
    const vars = seasonCssVars(painted);
    const before = Object.keys(vars).map((name) => [name, el.style.getPropertyValue(name)] as const);
    el.setAttribute('data-g-season', painted);
    for (const [name, value] of Object.entries(vars)) el.style.setProperty(name, value);
    return () => {
      el.removeAttribute('data-g-season');
      for (const [name, value] of before) {
        if (value) el.style.setProperty(name, value);
        else el.style.removeProperty(name);
      }
    };
  }, [painted]);

  // GOOGIE-6 — "only glow that strong when you hover over it or
  // scroll on it." Hover and focus are CSS; this is the scroll half.
  // AURA-2 (Rennan: "when you interact with the bar, it gets its color
  // and then slowly fades the color once not interacting with it") —
  // using the bar — a tap, a touch sliding along it, a wheel over it —
  // not scrolling the page: reading a long page isn't using the bar,
  // and lighting it on every scroll was the glow he asked to tone down.
  // The class stays on while that goes on, plus a beat; the fade out is
  // CSS, and slow. (A mouse lights it through :hover instead.)
  useEffect(() => {
    if (bare) return;
    const el = document.documentElement;
    let timer = 0;
    const wake = (event: Event) => {
      const target = event.target as Element | null;
      if (!target?.closest?.("nav[aria-label='Aperture Bar']")) return;
      el.classList.add('g-aura-live');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => el.classList.remove('g-aura-live'), 900);
    };
    window.addEventListener('pointerdown', wake, { passive: true });
    window.addEventListener('wheel', wake, { passive: true });
    window.addEventListener('touchmove', wake, { passive: true });
    return () => {
      window.clearTimeout(timer);
      el.classList.remove('g-aura-live');
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('wheel', wake);
      window.removeEventListener('touchmove', wake);
    };
  }, [bare]);
  const [pinned, setPinned] = useState<Tod | null>(null);
  const clock = useTodClock();
  const tod = pinned ?? clock;
  const vars = useMemo(() => {
    const sky = painted ? { ...skyVars(tod, false), ...seasonCssVars(painted) } : skyVars(tod, light);
    // The bar's light follows the season in either mode.
    return season ? { ...sky, '--g-season-aura': SEASON_AURA[season] } : sky;
  }, [tod, light, painted, season]);

  const value = useMemo<GoogieSkyValue>(
    () => ({
      tod,
      sky: SKIES[tod],
      light,
      season,
      // `bare` is set in exactly one place — RootLayout's sidebar
      // branch — so it is the same question as "is the rail up?".
      railed: bare,
      setTod: setPinned,
      cycle: () => setPinned(TOD_ORDER[(TOD_ORDER.indexOf(tod) + 1) % TOD_ORDER.length]),
    }),
    [tod, light, season, bare],
  );

  return (
    <GoogieSkyContext.Provider value={value}>
      <div
        /* `dark` rides along so the app's own components — every page
           nobody has rebuilt yet, plus the Aperture Bar — resolve
           their dark-mode variants. The token values those variables
           carry are overridden to the redesign's palette in
           googie.css, so "dark" here means the redesign's dark, not
           the old cosmic one. */
        className={`googie dark relative flex min-h-svh w-full flex-col overflow-x-hidden ${className ?? ''}`}
        data-g-mode={light ? 'light' : 'dark'}
        data-g-season={painted ?? undefined}
        data-g-chrome={bare ? 'sidebar' : 'aperture'}
        style={{ ...vars, background: 'var(--g-sky)' } as React.CSSProperties}
      >
        {/* SEASON-1 — a season's painting replaces all four: the gradient
            would be hidden under it, and stars, a burst and a comet
            belong to the Googie sky, not a snowy village. */}
        {painted ? (
          <SeasonalSky season={painted} tod={tod} />
        ) : (
          <>
            {/* Stars — three parallax layers, breathing on a 9s cycle. */}
            <div
              aria-hidden
              className="pointer-events-none fixed inset-0"
              style={{
                opacity: 'var(--g-stars)',
                backgroundImage:
                  'radial-gradient(circle at 18% 22%,rgba(255,255,255,.9) 0 .8px,transparent 1.4px),radial-gradient(circle at 62% 71%,rgba(255,255,255,.7) 0 .7px,transparent 1.3px),radial-gradient(circle at 84% 34%,rgba(255,255,255,.85) 0 1px,transparent 1.6px)',
                backgroundSize: '190px 160px,270px 230px,340px 290px',
                animation: 'g-twinkle 9s ease-in-out infinite',
              }}
            />

            {/* The corner glow, in the sky's own colour. */}
            <div
              aria-hidden
              className="pointer-events-none fixed"
              style={{
                right: '-180px',
                top: '-200px',
                width: '620px',
                height: '620px',
                borderRadius: '50%',
                background: 'radial-gradient(circle,var(--g-glow) 0%,transparent 60%)',
                opacity: 0.24,
              }}
            />

            {/* Atomic starburst — the Googie signature, one slow rotation.
                It lives in the gutter beside the content column, so it only
                appears once the viewport is wide enough to have a gutter:
                any narrower and it sits on top of a screen's own controls. */}
            <svg
              aria-hidden
              data-g-burst
              width="120"
              height="120"
              viewBox="0 0 120 120"
              className="pointer-events-none fixed hidden 2xl:block"
              style={{
                right: 'max(12px, calc(50vw - 720px))',
                top: '90px',
                opacity: 0.5,
                animation: 'g-spin 160s linear infinite',
              }}
            >
              <g stroke="#F4EFE6" strokeWidth="1.4" strokeLinecap="round">
                <path d="M60 8v104M8 60h104M23 23l74 74M97 23l-74 74" />
              </g>
              <circle cx="60" cy="8" r="3" fill="#E1B24C" />
              <circle cx="112" cy="60" r="3" fill="#E1B24C" />
              <circle cx="60" cy="112" r="3" fill="#E1B24C" />
              <circle cx="8" cy="60" r="3" fill="#E1B24C" />
              <circle cx="60" cy="60" r="6" fill="#E63B2E" />
            </svg>

            {/* A comet, every 34 seconds, only when the sky is dark enough. */}
            <div aria-hidden className="pointer-events-none fixed inset-0" style={{ opacity: 'var(--g-comet)', transition: 'opacity 1.5s' }}>
              <div
                style={{
                  position: 'absolute',
                  right: '80px',
                  top: '150px',
                  width: '140px',
                  height: '1.5px',
                  borderRadius: '2px',
                  background: 'linear-gradient(90deg,#FFF1C2,transparent)',
                  transform: 'rotate(-30deg)',
                  opacity: 0,
                  animation: 'g-shoot 34s cubic-bezier(.3,0,.7,1) infinite 2s',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: '-2px',
                    top: '-2px',
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    background: '#FFF1C2',
                    boxShadow: '0 0 10px #FFF1C2',
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* GOOGIE-8 — the clearance the Aperture Bar needs is NOT
            padding on this wrapper any more. It used to be `pb-32`,
            and padding here sits OUTSIDE the page's own background
            box: any page that paints its own full-height background —
            Passport's starfield, the Sites admin's light canvas —
            ended 128px early and the sky showed through underneath as
            a band. Rennan: "certain pages UI doesn't extend all the
            way." Correct, and this was why.

            The clearance now goes to the page itself, in googie.css,
            so the page's own background grows with it and there is no
            seam to see. `data-g-content` is what that rule hangs on. */}
        {/* GOOGIE-FROST-1 — a dark frosted pane between the sky and the
            page.

            Rennan: "on every page other than Nova and Calendar the cool
            themed backgrounds are way too distracting. rather than get
            rid of them, lets give a dark frosted background on top."
            Which is the right call: the painted seasons are some of the
            best work in the app, and on Today they read as atmosphere —
            but behind a settings form or a wall of gallery thumbnails
            they are competing with the thing you came to use. A pane,
            not a deletion: the season still shows through it, softened
            and pushed back where it belongs.

            Nova and Calendar keep the sky at full strength. Nova IS the
            atmosphere, and the calendar's own surfaces are opaque
            enough that the sky only ever shows at its edges.

            `backdrop-filter` does the work, so it blurs whatever the
            shell happens to be painting — gradient, stars and comet, or
            a season's painting — without this layer needing to know
            which. The fill behind it is what makes it dark; blur alone
            would leave a bright scene bright. */}
        {frosted && (
          <div
            aria-hidden
            data-g-frost
            className="pointer-events-none fixed inset-0 z-0"
            style={{
              background: 'rgba(9,10,12,0.58)',
              backdropFilter: 'blur(28px) saturate(118%)',
              WebkitBackdropFilter: 'blur(28px) saturate(118%)',
            }}
          />
        )}
        <div data-g-content className="relative z-[1] flex min-h-0 flex-1 flex-col">
          {children}
        </div>
        {/* GOOGIE-2 — the Aperture Bar, Orbit's own. The redesign drew
            its own pill rail for a while; Rennan wants the bar back,
            and he's right: it is the app's navigation, studios know
            its gestures, and a redesign has no business replacing a
            control people have already learned. It carries the app's
            tokens, so it renders inside its own dark scope rather
            than inheriting the sky's. */}
        {!bare && <AppApertureBar />}
      </div>
    </GoogieSkyContext.Provider>
  );
}

/**
 * The wrapper a REDESIGNED screen returns. It carries the redesign's
 * type and colour; a page that hasn't been redesigned yet doesn't use
 * it, and so keeps the app's own palette while still getting the sky
 * and the rail from the shell.
 */
export function GoogieScreen({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`g-screen ${className ?? ''}`}>{children}</div>;
}

/** The clock, re-checked every five minutes so a session left open
 *  at dusk is under a night sky by the time it's used again. */
function useTodClock(): Tod {
  const [tod, setTod] = useState<Tod>(() => resolveTod());
  useEffect(() => {
    const id = window.setInterval(() => setTod(resolveTod()), 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, []);
  return tod;
}

export { SKIES };
export default GoogieShell;
